import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { I18nextProvider } from 'react-i18next';
import './index.css'
import App from './App.tsx'
import i18n from './i18n';

/**
 * Suppress benign "Failed to execute 'removeChild'/'insertBefore' on 'Node'"
 * DOM errors caused by:
 *   - browser auto-translate (Google Translate / Chrome translate widget) and
 *     text extensions (Grammarly, LastPass, etc.) that wrap/move React-managed
 *     text nodes, and
 *   - race conditions between Radix UI portal animations and React's reconciler
 *     during state updates (e.g., polling on /dashboard and /documents).
 * When React later re-renders or removes those nodes it can no longer find them
 * and throws NotFoundError. This is a well-known React issue — the reconciler
 * recovers and app state remains consistent, so we silence it here and filter
 * it out of Sentry (see lib/sentry.ts).
 */
const BENIGN_DOM_PATTERNS = [
  'removeChild',
  'insertBefore',
  'not a child',
  'The node before which',
];

function isBenignDomMutationError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  return BENIGN_DOM_PATTERNS.some((pattern) => msg.includes(pattern));
}

// React 19 onRecoverableError — handles commit-phase DOM errors from the reconciler
const onRecoverableError = (error: unknown) => {
  if (isBenignDomMutationError(error)) {
    console.warn('[React] Suppressed benign DOM mutation error (portal/animation race or browser extension):',
      error instanceof Error ? error.message : error);
    return;
  }
  // Re-throw other recoverable errors so they surface normally
  console.error('[React] Recoverable error:', error);
};

// Global handler for uncaught DOM errors (browser extension interference)
window.addEventListener('error', (event: ErrorEvent) => {
  if (isBenignDomMutationError(event.message)) {
    event.preventDefault();
    console.warn('[DOM] Suppressed benign DOM mutation error (likely browser extension interference):',
      event.message);
  }
});

// Also catch unhandled promise rejections that may wrap the same DOM error
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  if (isBenignDomMutationError(event.reason?.message)) {
    event.preventDefault();
    console.warn('[DOM] Suppressed async benign DOM mutation error:', event.reason?.message);
  }
});

createRoot(document.getElementById('root')!, { onRecoverableError }).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <App />
    </I18nextProvider>
  </StrictMode>,
)
